import React, { useState, useEffect } from "react";
import styled, { keyframes, css } from "styled-components";
import { useWebsiteTheme } from "../WebsiteTheme/WebsiteThemeContext";
import { Theme } from "../WebsiteThemeElements";
import { BackgroundWrapper, Container, LoaderWrapper } from "./LoadingScreenElements";

// --- DEFAULT LOGO PATH PRESETS ---
const PRESETS = {
  swerlLogo: {
    name: "Swerl Stylized 'S'",
    viewBox: "0 0 100 100",
    path: "M50,15 C30,15 20,30 20,45 C20,65 50,60 50,75 C50,85 40,85 30,85 C20,85 15,80 15,75 M50,85 C70,85 80,70 80,55 C80,35 50,40 50,25 C50,15 60,15 70,15 C80,15 85,20 85,25",
    strokeWidth: 8,
    fill: "none",
  },
  headphones: {
    name: "Swerl Audio Gear",
    viewBox: "0 0 24 24",
    path: "M3 14c0-4.97 4.03-9 9-9s9 4.03 9 9v3c0 1.66-1.34 3-3 3h-2v-8h3v-2c0-3.87-3.13-7-7-7S5 7.13 5 11v2h3v8H6c-1.66 0-3-1.34-3-3v-3z",
    strokeWidth: 1.5,
    fill: "currentColor",
  },
  waves: {
    name: "Sonar Resonance",
    viewBox: "0 0 24 24",
    path: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z",
    strokeWidth: 0,
    fill: "currentColor",
  },
};

// --- KEYFRAME ANIMATIONS ---
const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); filter: drop-shadow(0 0 10px rgba(255, 105, 120, 0.2)); }
  50% { transform: scale(1.04); filter: drop-shadow(0 0 25px rgba(255, 105, 120, 0.5)); }
`;

const rotatingRing = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const bounceWave = keyframes`
  0%, 100% { transform: translateY(0); opacity: 0.3; }
  50% { transform: translateY(-8px); opacity: 1; }
`;

const rippleRing = keyframes`
  0% { transform: scale(0.9); opacity: 0.8; }
  100% { transform: scale(1.4); opacity: 0; }
`;

// --- STYLED COMPONENTS ---
const SandboxWrapper = styled.div`
  min-height: 100vh;
  color: ${(props) => props.theme.secondaryBg};
  background-color: ${(props) => props.theme.mainBg};
  font-family:
    ui-sans-serif,
    system-ui,
    -apple-system,
    sans-serif;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  align-content: center;
`;

const ControlDeck = styled.div`
  background-color: ${(props) => props.theme.secondaryBg};
  border-right: 1px solid #1e293b;
  padding: 32px;
  box-sizing: border-box;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 900;
  margin: 0 0 8px 0;
  background: linear-gradient(
    to right,
    #fff,
    ${(props) => props.theme.accentColor}
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  font-size: 0.8rem;
  color: #94a3b8;
  line-height: 1.5;
  margin: 0 0 24px 0;
`;

const Label = styled.span`
  font-size: 0.7rem;
  color: #64748b;
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.05em;
  display: block;
  margin-bottom: 6px;
`;

const PresetBadgeGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
`;

const PresetBadge = styled.button`
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  background-color: ${(props) =>
    props.active
      ? (props) => props.theme.mainColor
      : (props) => props.theme.background};
  color: ${(props) => (props.active ? "#080c16" : "#cbd5e1")};
  border: ${(props) =>
    props.active
      ? `1px solid ${(props) => props.theme.mainColor}`
      : "1px solid #1e293b"};
  transition: all 0.2s ease;

  &:hover {
    filter: brightness(1.1);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  background-color: ${(props) => props.theme.background};
  border: 1px solid #334155;
  border-radius: 10px;
  padding: 12px;
  color: #fff;
  font-family: monospace;
  font-size: 0.8rem;
  min-height: 100px;
  margin-bottom: 20px;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.mainColor};
  }
`;

const InputGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
`;

const Input = styled.input`
  width: 100%;
  background-color: ${(props) => props.theme.background};
  border: 1px solid #334155;
  border-radius: 10px;
  padding: 12px;
  color: #fff;
  font-size: 0.85rem;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.mainColor};
  }
`;

const Select = styled.select`
  width: 100%;
  background-color: ${(props) => props.theme.background};
  border: 1px solid #334155;
  border-radius: 10px;
  padding: 12px;
  color: #fff;
  font-size: 0.85rem;
  margin-bottom: 20px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.secondaryColor};
  }
`;

const PreviewScreen = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  align-items: center;
  align-content: center;
  justify-content: center;
  position: relative;
  padding: 40px;
  box-sizing: border-box;
  background: radial-gradient(
    circle at center,
    ${(props) => props.theme.mainBg} 0%,
    ${(props) => props.theme.background} 100%
  );
  overflow: hidden;
`;

const WatermarkIndicator = styled.div`
  position: absolute;
  top: 24px;
  left: 24px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: rgba(255, 255, 255, 0.2);
`;

const RadarRippleRing = styled.div`
  position: absolute;
  width: 280px;
  height: 280px;
  border-radius: 50%;
  border: 1px solid ${(props) => props.theme.mainColor};
  opacity: 0;
  pointer-events: none;
  animation: ${rippleRing} 2s infinite cubic-bezier(0.16, 1, 0.3, 1);
`;

const LogoOuterGlowRing = styled.div`
  width: 160px;
  height: 160px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-shadow: 0 0 40px rgba(255, 105, 120, 0.15);
  border: 2px solid rgba(255, 105, 120, 0.05);
  margin-bottom: 32px;
`;

const SpinningSvg = styled.svg`
  position: absolute;
  inset: -10px;
  width: 180px;
  height: 180px;
  animation: ${rotatingRing} 4s linear infinite;
`;

const InnerLogoPulsing = styled.div`
  width: 25rem;
  height: 25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${pulse} ${(props) => props.glowSpeed || 2}s infinite ease-in-out;
`;

const VectorSvg = styled.svg`
 right: 30%;
 width: 25rem;
  height: 25rem;
  display: flex;
align-self: center;
justify-self: center;
align-items: center;
justiyfy-content: center;
  color: ${(props) => props.theme.mainColor};
  transition: all 0.3s ease;
`;

const ProgressPercentage = styled.div`
  font-size: 1.75rem;
  font-weight: 900;
  color: #fff;
  letter-spacing: -0.02em;
  margin-bottom: 8px;
`;

const StatusBarContainer = styled.div`
  width: 280px;
  height: 4px;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 9999px;
  overflow: hidden;
  margin-bottom: 16px;
  border: 1px solid rgba(255, 255, 255, 0.02);
`;

const StatusBarProgress = styled.div`
  width: ${(props) => props.progress}%;
  height: 100%;
  background: linear-gradient(
    to right,
    ${(props) => props.theme.mainColor},
    ${(props) => props.theme.secondaryColor}
  );
  border-radius: 9999px;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

const StatusFeedbackWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 20px;
`;

const WaveBarGroup = styled.div`
  display: inline-block;
`;

const WaveBar = styled.span`
  width: 3px;
  height: 16px;
  background-color: ${(props) => props.theme.accentColor};
  border-radius: 9999px;
  display: inline-block;
  margin: 0 2px;
  animation: ${bounceWave} 1s infinite ease-in-out;
  animation-delay: ${(props) => props.delay || "0s"};
`;

const StatusTextLabel = styled.span`
  font-size: 0.8rem;
  color: #94a3b8;
  font-weight: 600;
  transition: color 0.2s ease;
`;

const ActionButton = styled.button`
  background-color: ${(props) => props.theme.mainColor};
  color: #080c16;
  font-weight: 800;
  border: none;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: ${(props) => props.marginTop || "0px"};

  &:hover {
    transform: translateY(-1px);
    opacity: 0.95;
  }

  &:active {
    transform: scale(0.95);
  }

  ${(props) =>
    props.animate &&
    css`
      animation: ${slideUp} 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    `}
`;

const ToastNotification = styled.div`
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 100;
  max-width: 384px;
  width: calc(100% - 48px);
  background-color: #131a30;
  border: 1px solid
    ${(props) =>
      props.type === "error"
        ? (props) => props.theme.errorColor
        : props.type === "warning"
          ? (props) => props.theme.warningColor
          : (props) => props.theme.successColor};
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  animation: ${slideUp} 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
`;

const ToastHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ToastTitle = styled.h4`
  font-weight: 700;
  font-size: 0.95rem;
  margin: 0;
  color: ${(props) =>
    props.type === "error"
      ? (props) => props.theme.errorColor
      : props.type === "warning"
        ? (props) => props.theme.warningColor
        : (props) => props.theme.successColor};
`;

const ToastText = styled.p`
  font-size: 0.825rem;
  color: #94a3b8;
  margin: 6px 0 0 0;
  line-height: 1.4;
`;

const ToastClose = styled.button`
  color: #64748b;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 0.75rem;
  &:hover {
    color: #fff;
  }
`;

// --- MAIN PORTAL COMPONENT ---
export default function LoadingScreen({transparent}) {
  const [selectedPreset, setSelectedPreset] = useState("swerlLogo");
  const [customPath, setCustomPath] = useState(PRESETS.swerlLogo.path);
  const [viewBox, setViewBox] = useState(PRESETS.swerlLogo.viewBox);
  const [strokeWidth, setStrokeWidth] = useState(PRESETS.swerlLogo.strokeWidth);
  const [fillMode, setFillMode] = useState(PRESETS.swerlLogo.fill);

  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isDone, setIsDone] = useState(false);
  const [glowSpeed, setGlowSpeed] = useState(2);
  const [loadingStatusText, setLoadingStatusText] = useState(
    "Initializing Swerl Core...",
  );
  const [notification, setNotification] = useState(null);

  const applyPreset = (key) => {
    setSelectedPreset(key);
    setCustomPath(PRESETS[key].path);
    setViewBox(PRESETS[key].viewBox);
    setStrokeWidth(PRESETS[key].strokeWidth);
    setFillMode(PRESETS[key].fill);
  };

  useEffect(() => {
    let interval = null;
    if (loadingProgress < 100) {
      interval = setInterval(() => {
        setLoadingProgress((prev) => {
          const next = prev + Math.floor(Math.random() * 8) + 1;
          const capped = next > 100 ? 100 : next;

          if (capped < 25) {
            setLoadingStatusText("Connecting to XRPL Gateway...");
          } else if (capped < 50) {
            setLoadingStatusText("Validating Sonar Muse ledger channels...");
          } else if (capped < 80) {
            setLoadingStatusText("Unlocking real-time streaming pipelines...");
          } else if (capped < 100) {
            setLoadingStatusText("Buffering audio allocation balances...");
          } else {
            setLoadingStatusText("Swerl engine is fully operational!");
            setIsDone(true);
          }
          return capped;
        });
      }, 250);
    }
    return () => clearInterval(interval);
  }, [loadingProgress]);

  const restartLoading = () => {
    setLoadingProgress(0);
    setIsDone(false);
    setLoadingStatusText("Re-initializing Swerl Core...");
  };

  return (
    <Container>
				<BackgroundWrapper transparent={transparent} color={Theme.mainBg}>
					<LoaderWrapper>
        <RadarRippleRing />

        <LogoOuterGlowRing>
          <SpinningSvg viewBox="0 0 100 100">
            <circle
              cx="50"
              cy="50"
              r="46"
              fill="none"
              stroke={`${Theme.mainColor}20`}
              strokeWidth="1"
            />
            <circle
              cx="50"
              cy="50"
              r="46"
              fill="none"
              stroke={Theme.mainColor}
              strokeWidth="3"
              strokeDasharray="25 80"
            />
            <circle
              cx="50"
              cy="50"
              r="46"
              fill="none"
              stroke={Theme.secondaryColor}
              strokeWidth="3"
              strokeDasharray="15 150"
              strokeDashoffset="50"
            />
          </SpinningSvg>

          <InnerLogoPulsing glowSpeed={glowSpeed}>
            <VectorSvg fill="none" viewBox="0 0 1429 1417">
              <path
			  style={{display: "flex", alignSelf: "center", justifySelf: "center"}}
                d="M 642.00,575.41
           C 642.00,575.41 644.77,582.67 644.77,582.67
             629.19,589.91 611.49,597.37 591.66,605.05
             591.66,605.05 586.14,607.17 586.14,607.17
             586.99,602.20 587.84,596.17 588.69,589.06
             589.39,583.38 590.03,578.33 590.59,573.92
             591.45,567.81 591.88,562.84 591.88,559.00
             591.88,550.34 587.77,546.00 579.56,546.00
             566.25,546.00 550.53,551.90 532.41,563.69
             508.05,579.46 495.88,597.71 495.88,618.45
             495.88,625.98 499.09,630.92 505.53,633.27
             511.98,635.61 525.75,636.78 546.84,636.78
             546.84,636.78 549.61,636.78 549.61,636.78
             550.04,632.24 550.25,629.12 550.25,627.41
             550.25,615.34 548.55,604.76 545.16,595.67
             559.31,599.08 566.39,611.57 566.39,633.16
             566.39,633.16 566.39,636.78 566.39,636.78
             566.39,636.78 599.73,636.00 599.73,636.00
             615.17,636.00 626.08,637.14 632.45,639.41
             653.55,646.92 664.09,662.37 664.09,685.75
             664.09,717.22 652.34,747.41 628.83,776.33
             600.80,810.78 566.60,828.00 526.25,828.00
             496.66,828.00 474.14,815.95 458.70,791.86
             423.59,813.95 386.41,825.00 347.19,825.00
             317.03,825.00 289.56,818.20 264.78,804.61
             216.49,778.01 192.34,732.57 192.34,668.30
             192.34,627.53 203.18,594.12 224.84,568.08
             246.51,542.03 274.19,529.00 307.89,529.00
             340.61,529.00 368.79,539.55 392.44,560.64
             405.60,572.25 416.22,586.44 424.30,603.22
             432.37,619.99 441.57,646.78 451.91,683.59
             451.91,683.59 462.53,721.81 462.53,721.81
             468.33,742.49 473.57,758.14 478.25,768.75
             492.27,760.41 506.32,745.28 520.41,723.38
             534.49,701.47 543.16,681.26 546.42,662.73
             546.42,662.73 536.66,663.00 536.66,663.00
             513.00,663.00 496.32,661.05 486.62,657.14
             476.93,653.23 472.08,646.45 472.08,636.80
             472.08,621.17 480.64,603.35 497.78,583.33
             511.52,567.28 527.73,554.67 546.42,545.52
             565.12,536.36 589.27,529.23 618.86,524.11
             618.86,524.11 626.08,522.83 626.08,522.83
             626.08,522.83 625.44,526.25 625.44,526.25
             624.02,532.66 622.89,538.35 622.05,543.33
             622.05,543.33 618.64,563.42 618.64,563.42
             617.65,569.83 617.16,573.96 617.16,575.81
             617.16,579.94 618.85,582.00 622.25,582.00
             624.66,582.00 627.13,581.49 629.69,580.47
             635.34,578.12 639.45,576.43 642.00,575.41 Z
           M 562.56,662.31
           C 557.88,682.11 547.90,702.81 532.61,724.44
             517.32,746.06 500.33,763.52 481.64,776.80
             488.58,791.61 496.22,802.02 504.58,808.02
             512.93,814.01 523.84,817.00 537.30,817.00
             564.62,817.00 588.80,805.73 609.83,783.20
             630.86,760.66 641.38,734.79 641.38,705.59
             641.38,689.15 636.80,677.77 627.67,671.47
             618.54,665.16 602.14,662.00 578.50,662.00
             578.50,662.00 567.88,663.00 567.88,663.00
             567.88,663.00 562.56,662.31 562.56,662.31 Z
           M 455.09,784.23
           C 448.29,769.26 443.27,755.83 440.02,743.95
             440.02,743.95 423.02,681.62 423.02,681.62
             409.57,632.01 393.35,596.01 374.38,573.61
             355.41,551.20 331.76,540.00 303.44,540.00
             273.84,540.00 250.72,549.88 234.08,569.62
             217.44,589.38 209.12,616.74 209.12,651.72
             209.12,669.92 212.48,688.33 219.20,706.95
             225.93,725.57 235.04,741.72 246.52,755.41
             260.95,772.62 278.44,786.02 298.97,795.61
             319.50,805.20 341.02,810.00 363.55,810.00
             393.70,810.00 424.22,801.41 455.09,784.23 Z"
                fill="black"
				
              />
            </VectorSvg>
          </InnerLogoPulsing>
        </LogoOuterGlowRing>

        {/*   <StatusBarContainer>
          <StatusBarProgress progress={loadingProgress} />
        </StatusBarContainer> */}

        <StatusFeedbackWrapper>
          {!isDone && (
            <WaveBarGroup>
              <WaveBar delay="0.1s" />
              <WaveBar delay="0.2s" />
              <WaveBar delay="0.3s" />
              <WaveBar delay="0.4s" />
              <WaveBar delay="0.5s" />
            </WaveBarGroup>
          )}
          <StatusTextLabel>{loadingStatusText}</StatusTextLabel>
        </StatusFeedbackWrapper>
		</LoaderWrapper>
			</BackgroundWrapper>
      </Container>
				
					
  );
}

export function ModalLoadingScreen() {
  const [selectedPreset, setSelectedPreset] = useState("swerlLogo");
  const [customPath, setCustomPath] = useState(PRESETS.swerlLogo.path);
  const [viewBox, setViewBox] = useState(PRESETS.swerlLogo.viewBox);
  const [strokeWidth, setStrokeWidth] = useState(PRESETS.swerlLogo.strokeWidth);
  const [fillMode, setFillMode] = useState(PRESETS.swerlLogo.fill);

  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isDone, setIsDone] = useState(false);
  const [glowSpeed, setGlowSpeed] = useState(2);
  const [loadingStatusText, setLoadingStatusText] = useState(
    "Initializing Swerl Core...",
  );
  const [notification, setNotification] = useState(null);

  const applyPreset = (key) => {
    setSelectedPreset(key);
    setCustomPath(PRESETS[key].path);
    setViewBox(PRESETS[key].viewBox);
    setStrokeWidth(PRESETS[key].strokeWidth);
    setFillMode(PRESETS[key].fill);
  };

  useEffect(() => {
    let interval = null;
    if (loadingProgress < 100) {
      interval = setInterval(() => {
        setLoadingProgress((prev) => {
          const next = prev + Math.floor(Math.random() * 8) + 1;
          const capped = next > 100 ? 100 : next;

          if (capped < 25) {
            setLoadingStatusText("Connecting to XRPL Gateway...");
          } else if (capped < 50) {
            setLoadingStatusText("Validating Sonar Muse ledger channels...");
          } else if (capped < 80) {
            setLoadingStatusText("Unlocking real-time streaming pipelines...");
          } else if (capped < 100) {
            setLoadingStatusText("Buffering audio allocation balances...");
          } else {
            setLoadingStatusText("Swerl engine is fully operational!");
            setIsDone(true);
          }
          return capped;
        });
      }, 250);
    }
    return () => clearInterval(interval);
  }, [loadingProgress]);

  const restartLoading = () => {
    setLoadingProgress(0);
    setIsDone(false);
    setLoadingStatusText("Re-initializing Swerl Core...");
  };

  return (
    <>
	<BackgroundWrapper transparent={true} color={Theme.mainBg} style={{padding: "2%"}}>
					<LoaderWrapper>
      <RadarRippleRing />

      <LogoOuterGlowRing>
        <SpinningSvg viewBox="0 0 100 100">
          <circle
            cx="50"
            cy="50"
            r="46"
            fill="none"
            stroke={`${Theme.mainColor}20`}
            strokeWidth="1"
          />
          <circle
            cx="50"
            cy="50"
            r="46"
            fill="none"
            stroke={Theme.mainColor}
            strokeWidth="3"
            strokeDasharray="25 80"
          />
          <circle
            cx="50"
            cy="50"
            r="46"
            fill="none"
            stroke={Theme.secondaryColor}
            strokeWidth="3"
            strokeDasharray="15 150"
            strokeDashoffset="50"
          />
        </SpinningSvg>

                 <InnerLogoPulsing glowSpeed={glowSpeed}>
            <VectorSvg fill="none" viewBox="0 0 1429 1417">
              <path
			  style={{display: "flex", alignSelf: "center", justifySelf: "center"}}
                d="M 642.00,575.41
           C 642.00,575.41 644.77,582.67 644.77,582.67
             629.19,589.91 611.49,597.37 591.66,605.05
             591.66,605.05 586.14,607.17 586.14,607.17
             586.99,602.20 587.84,596.17 588.69,589.06
             589.39,583.38 590.03,578.33 590.59,573.92
             591.45,567.81 591.88,562.84 591.88,559.00
             591.88,550.34 587.77,546.00 579.56,546.00
             566.25,546.00 550.53,551.90 532.41,563.69
             508.05,579.46 495.88,597.71 495.88,618.45
             495.88,625.98 499.09,630.92 505.53,633.27
             511.98,635.61 525.75,636.78 546.84,636.78
             546.84,636.78 549.61,636.78 549.61,636.78
             550.04,632.24 550.25,629.12 550.25,627.41
             550.25,615.34 548.55,604.76 545.16,595.67
             559.31,599.08 566.39,611.57 566.39,633.16
             566.39,633.16 566.39,636.78 566.39,636.78
             566.39,636.78 599.73,636.00 599.73,636.00
             615.17,636.00 626.08,637.14 632.45,639.41
             653.55,646.92 664.09,662.37 664.09,685.75
             664.09,717.22 652.34,747.41 628.83,776.33
             600.80,810.78 566.60,828.00 526.25,828.00
             496.66,828.00 474.14,815.95 458.70,791.86
             423.59,813.95 386.41,825.00 347.19,825.00
             317.03,825.00 289.56,818.20 264.78,804.61
             216.49,778.01 192.34,732.57 192.34,668.30
             192.34,627.53 203.18,594.12 224.84,568.08
             246.51,542.03 274.19,529.00 307.89,529.00
             340.61,529.00 368.79,539.55 392.44,560.64
             405.60,572.25 416.22,586.44 424.30,603.22
             432.37,619.99 441.57,646.78 451.91,683.59
             451.91,683.59 462.53,721.81 462.53,721.81
             468.33,742.49 473.57,758.14 478.25,768.75
             492.27,760.41 506.32,745.28 520.41,723.38
             534.49,701.47 543.16,681.26 546.42,662.73
             546.42,662.73 536.66,663.00 536.66,663.00
             513.00,663.00 496.32,661.05 486.62,657.14
             476.93,653.23 472.08,646.45 472.08,636.80
             472.08,621.17 480.64,603.35 497.78,583.33
             511.52,567.28 527.73,554.67 546.42,545.52
             565.12,536.36 589.27,529.23 618.86,524.11
             618.86,524.11 626.08,522.83 626.08,522.83
             626.08,522.83 625.44,526.25 625.44,526.25
             624.02,532.66 622.89,538.35 622.05,543.33
             622.05,543.33 618.64,563.42 618.64,563.42
             617.65,569.83 617.16,573.96 617.16,575.81
             617.16,579.94 618.85,582.00 622.25,582.00
             624.66,582.00 627.13,581.49 629.69,580.47
             635.34,578.12 639.45,576.43 642.00,575.41 Z
           M 562.56,662.31
           C 557.88,682.11 547.90,702.81 532.61,724.44
             517.32,746.06 500.33,763.52 481.64,776.80
             488.58,791.61 496.22,802.02 504.58,808.02
             512.93,814.01 523.84,817.00 537.30,817.00
             564.62,817.00 588.80,805.73 609.83,783.20
             630.86,760.66 641.38,734.79 641.38,705.59
             641.38,689.15 636.80,677.77 627.67,671.47
             618.54,665.16 602.14,662.00 578.50,662.00
             578.50,662.00 567.88,663.00 567.88,663.00
             567.88,663.00 562.56,662.31 562.56,662.31 Z
           M 455.09,784.23
           C 448.29,769.26 443.27,755.83 440.02,743.95
             440.02,743.95 423.02,681.62 423.02,681.62
             409.57,632.01 393.35,596.01 374.38,573.61
             355.41,551.20 331.76,540.00 303.44,540.00
             273.84,540.00 250.72,549.88 234.08,569.62
             217.44,589.38 209.12,616.74 209.12,651.72
             209.12,669.92 212.48,688.33 219.20,706.95
             225.93,725.57 235.04,741.72 246.52,755.41
             260.95,772.62 278.44,786.02 298.97,795.61
             319.50,805.20 341.02,810.00 363.55,810.00
             393.70,810.00 424.22,801.41 455.09,784.23 Z"
                fill="black"
				
              />
            </VectorSvg>
          </InnerLogoPulsing>
      </LogoOuterGlowRing>

      {/*   <StatusBarContainer>
          <StatusBarProgress progress={loadingProgress} />
        </StatusBarContainer> */}

      <StatusFeedbackWrapper>
        {!isDone && (
          <WaveBarGroup>
            <WaveBar delay="0.1s" />
            <WaveBar delay="0.2s" />
            <WaveBar delay="0.3s" />
            <WaveBar delay="0.4s" />
            <WaveBar delay="0.5s" />
          </WaveBarGroup>
        )}
        <StatusTextLabel>{loadingStatusText}</StatusTextLabel>
      </StatusFeedbackWrapper>
	  </LoaderWrapper>
	  </BackgroundWrapper>
    </>
  );
}
